import ExcelJS from 'exceljs';
import { cellText } from './classificationExcel';

export const GUIA_IMPORT_COL = {
  recetaCode: 'Código receta',
  recetaName: 'Nombre receta',
  quantity: 'Cantidad elaborada',
};

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet) {
  const must = [GUIA_IMPORT_COL.recetaCode, GUIA_IMPORT_COL.quantity];
  const maxScan = Math.min(15, sheet.rowCount || 15);
  for (let r = 1; r <= maxScan; r++) {
    const map = headerMapFromRow(sheet.getRow(r));
    if (must.every((label) => map[label] != null)) {
      return { rowIndex: r, colMap: map };
    }
  }
  return null;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

function parseQuantityStrict(raw) {
  const t = String(raw ?? '')
    .replace(/\s/g, '')
    .replace(',', '.');
  if (t === '') return { ok: false, error: 'vacía' };
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return { ok: false, error: 'no numérica' };
  if (n < 0) return { ok: false, error: 'negativa' };
  return { ok: true, value: Math.round(n * 10000) / 10000 };
}

/**
 * Descarga plantilla Excel con todas las recetas del formulario actual.
 * @param {{ rows: Array<{ receta_code, receta_name }>, guideTypeLabel: string }} opts
 */
export async function downloadProduccionGuiaTemplate({ rows, guideTypeLabel }) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Cantidades', { views: [{ state: 'frozen', ySplit: 2 }] });
  const headers = [GUIA_IMPORT_COL.recetaCode, GUIA_IMPORT_COL.recetaName, GUIA_IMPORT_COL.quantity];
  const lastCol = 'C';
  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value =
    `Guía ${guideTypeLabel}: complete solo «Cantidad elaborada». Fecha, hora, almacén y proyecto/operación se eligen en el formulario web. Fila 2 = encabezados; datos desde fila 3.`;
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = 56;
  headers.forEach((label, i) => {
    const c = ws.getRow(2).getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = HEADER_FILL;
  });
  ws.columns = [{ width: 16 }, { width: 36 }, { width: 18 }];
  (rows || []).forEach((r, idx) => {
    const out = ws.getRow(3 + idx);
    out.getCell(1).value = r.receta_code || '';
    out.getCell(2).value = r.receta_name || '';
    out.getCell(3).value = r.quantity != null && r.quantity !== '' ? Number(r.quantity) : '';
  });
  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(buf, `plantilla_guia_produccion_${Date.now()}.xlsx`);
}

/**
 * Lee Excel y devuelve mapa código receta (uppercase) -> cantidad.
 * @param {File} file
 * @param {Array<{ receta_plato_id, receta_code }>} formRows
 */
export async function parseProduccionGuiaQuantitiesImport(file, formRows) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const sheet = wb.worksheets[0];
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  const found = findHeaderRow(sheet);
  if (!found) {
    return {
      success: false,
      fatalMessage: `No se encontraron columnas «${GUIA_IMPORT_COL.recetaCode}» y «${GUIA_IMPORT_COL.quantity}».`,
    };
  }

  const { rowIndex, colMap } = found;
  const codeCol = colMap[GUIA_IMPORT_COL.recetaCode];
  const qtyCol = colMap[GUIA_IMPORT_COL.quantity];
  const last = getLastUsedRow(sheet);

  const byCode = new Map();
  const knownCodes = new Map(
    (formRows || []).map((r) => [String(r.receta_code || '').trim().toUpperCase(), r.receta_plato_id]),
  );

  const errors = [];
  let applied = 0;

  for (let r = rowIndex + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const code = cellText(row.getCell(codeCol)).trim().toUpperCase();
    const qtyRaw = cellText(row.getCell(qtyCol));
    if (!code && !qtyRaw) continue;
    if (!code) {
      errors.push(`Fila ${r}: falta código de receta.`);
      continue;
    }
    if (!knownCodes.has(code)) {
      errors.push(`Fila ${r}: código «${code}» no está en esta guía (${formRows?.length || 0} recetas).`);
      continue;
    }
    const qtyRes = parseQuantityStrict(qtyRaw);
    if (!qtyRes.ok) {
      errors.push(`Fila ${r} (${code}): cantidad ${qtyRes.error}.`);
      continue;
    }
    if (qtyRes.value === 0) continue;
    byCode.set(code, qtyRes.value);
    applied += 1;
  }

  if (applied === 0 && errors.length === 0) {
    return { success: false, fatalMessage: 'No hay cantidades mayores a cero en el archivo.' };
  }

  const updatedRows = (formRows || []).map((row) => {
    const key = String(row.receta_code || '').trim().toUpperCase();
    if (byCode.has(key)) {
      return { ...row, quantity: String(byCode.get(key)) };
    }
    return row;
  });

  return {
    success: true,
    rows: updatedRows,
    applied,
    errors,
  };
}
